import { useTranslation, Trans } from 'react-i18next'
import { Typography } from '@mui/material'
import EmailIcon from '@mui/icons-material/Email'
import { IconBox } from 'settle-ui/components/IconBox/IconBox'
import { Section } from 'settle-ui/components/Section/Section'
import { SectionItem } from 'settle-ui/components/SectionItem/SectionItem'
import { AuthCard } from 'routes/components/AuthCard/AuthCard'

export interface CheckEmailCardProps {
  email: string
}

/**
 * Confirmation step of the password reset flow. Deliberately offers no actions — the reset link lives
 * in the user's inbox, and the layout header still carries a route back into the app.
 */
export const CheckEmailCard: Settle.FC<CheckEmailCardProps> = ({ email }) => {
  const { t } = useTranslation()

  return (
    <AuthCard fullWidth={true} data-testid="submit-success-feedback">
      <Section fullWidth={true} sx={{ my: 'auto' }}>
        <SectionItem
          // This card replaces the form in place once the request resolves; without a live region a
          // screen-reader user gets no announcement that anything happened.
          role="status"
          fullWidth={true}
          horizontalGutter="none"
          verticalGutter="none"
          sx={{ gap: 1, alignItems: 'center' }}
        >
          <IconBox
            size="medium"
            sx={{
              mb: 1,
              backgroundColor: ({ tokens }) => tokens.ThemeColorCommonSecondaryContainer,
              color: ({ tokens }) => tokens.ThemeColorCommonSecondary,
              // IconBox pairs its 48px circle with a 32px glyph; the design draws a 24px one. `&&` because
              // IconBox sets the size through its own `& svg` rule, which a plain icon `sx` would lose to.
              '&& svg': { fontSize: '1.5rem' },
            }}
          >
            <EmailIcon />
          </IconBox>
          <Typography
            variant="h5"
            color="onSurfacePrimary"
            sx={{
              fontWeight: 700,
              textAlign: 'center',
            }}
          >
            {t('checkYourEmail')}
          </Typography>
          <Typography variant="body1" color="onSurfaceSecondary" sx={{ textAlign: 'center' }}>
            <Trans
              i18nKey="forgotPasswordPage::checkEmailSubtitle"
              values={{ email }}
              components={{ email: <Typography component="span" color="onSurfacePrimary" /> }}
            />
          </Typography>
        </SectionItem>
      </Section>
    </AuthCard>
  )
}
